import React, { useMemo } from "react";
import { useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { styled } from "@mui/material/styles";
import {
  EnvironmentVariables,
  OrganizationTierTypes,
} from "@/modules/common/types";
import Chip from "@mui/material/Chip";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import { muiTheme } from "@/styles/theme";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { FilterType } from "@/modules/common/constants";
import {
  managedOrganization,
  Organization,
} from "@/modules/common/dtos/index.v2";
import { useLocation, useSearchParams } from "react-router-dom";
import { queryClient } from "@/queryClient";

const StyledDropdown = styled(Dropdown)(({ screen }) => ({
  "& .dropdown-toggle": {
    backgroundColor: "transparent",
    height: "auto",
    minHeight: "40px",
    width: screen.isTablet || screen.isMobile ? "90vw" : "326px",
    justifyContent: "space-between",
    alignItems: "center",
    textAlign: "left",
    display: "inline-flex",
    border: `1px solid ${muiTheme.colorSchemes.light.palette.neutral[200]}`,
    borderRadius: "4px",
    outline: "none",
    color: muiTheme.colorSchemes.light.palette.neutral[500],
    padding: "6px 12px",
    flexWrap: "wrap",
    gap: "4px",
  },
  "& .dropdown-menu": {
    border: `1px solid ${muiTheme.colorSchemes.light.palette.background.blue1}`,
    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.175)",
    minWidth: "100%",
    whiteSpace: "nowrap",
  },
  "& .dropdown-item": {
    textAlign: "left",
    textTransform: "capitalize",
    "&:hover, &:focus": {
      backgroundColor: "#f8f9fa",
    },
    "&.selected": {
      backgroundColor: muiTheme.colorSchemes.light.palette.neutral[100],
    },
  },
  "&.active": {
    backgroundColor: "#d3d3d3",
    color: muiTheme.colorSchemes.light.palette.main.black,
  },
}));

interface OrganizationDropdownProps {
  filterType: FilterType;
  setFilterType: React.Dispatch<React.SetStateAction<FilterType>>;
  isMultiple?: boolean;
  isBecomeFranchiseOnly: boolean;
}

const OrganizationDropdown: React.FC<OrganizationDropdownProps> = ({
  filterType,
  setFilterType,
  isMultiple,
  isBecomeFranchiseOnly,
}) => {
  const screen = useScreenSize();

  const {
    selectedOrganization,
    filteredManagedOrganizations,
    selectedOrganizations,
    setSelectedOrganizations,
    setSelectedSingleOrganization,
    selectedSingleOrganization,
    userOrganizationsByManagers,
    selectedManagers,
  } = useOrganization();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationsList = useMemo(() => {
    if (selectedManagers?.length > 0) {
      return userOrganizationsByManagers?.data?.results.filter(
        (org: Organization) =>
          org?.tier?.type === EnvironmentVariables.COMPANY_NAME.toString() &&
          org?.tier
      );
    }

    return filteredManagedOrganizations;
  }, [
    selectedManagers,
    userOrganizationsByManagers,
    filteredManagedOrganizations,
  ]);

  useEffect(() => {
    if (isMultiple) {
      const storedOrgs = localStorage.getItem(`selectedOrganizations`);
      if (storedOrgs) {
        setSelectedOrganizations(JSON.parse(storedOrgs));
      }
    } else {
      const storedOrg = JSON.parse(
        localStorage.getItem(`selectedSingleOrganization`) || ""
      );
      if (storedOrg) {
        const selectedOrg = filteredManagedOrganizations?.find(
          (org) => org?.organization?.uid === storedOrg?.uid
        );
        if (selectedOrg) {
          setSelectedSingleOrganization(selectedOrg?.organization);
        }
      }
    }
  }, [
    filteredManagedOrganizations,
    isMultiple,
    setSelectedSingleOrganization,
    setSelectedOrganizations,
  ]);

  useEffect(() => {
    if (isBecomeFranchiseOnly) {
      const franchiseOrgs = organizationsList?.filter(
        (org: { organization: Organization }) =>
          org?.organization?.tier?.name?.toLowerCase() ===
          OrganizationTierTypes.FRANCHISE
      );

      if (isMultiple) {
        setSelectedOrganizations(
          franchiseOrgs?.map(
            (org: { organization: Organization }) => org.organization
          ) || []
        );
        if (pathname === "/" || pathname === "/leads") {
          queryClient.invalidateQueries({
            queryKey: ["leads"],
          });
        }
        if (pathname === "/sales") {
          queryClient.invalidateQueries({
            queryKey: ["getAllSales"],
          });
        }
        if (pathname === "/comments-search") {
          queryClient.invalidateQueries({
            queryKey: ["getCommentsSearchV2"],
          });
        }
        localStorage.setItem(
          `selectedOrganizations`,
          JSON.stringify(
            franchiseOrgs?.map(
              (org: { organization: Organization }) => org.organization
            ) || []
          )
        );
      }
    } else {
      setSelectedOrganizations(
        selectedOrganizations.filter(
          (org) =>
            org?.tier?.name?.toLowerCase() !== OrganizationTierTypes.FRANCHISE
        )
      );
    }
  }, [isBecomeFranchiseOnly, setFilterType, isMultiple, organizationsList]);

  const handleSelect = (eventKey: string | null) => {
    if (!eventKey || filterType === FilterType.ACCOUNT_MANAGER) return;

    const selectedOrg = filteredManagedOrganizations?.find(
      (org) => org?.organization?.uid === eventKey
    );

    if (!selectedOrg) return;

    if (isMultiple) {
      if (selectedOrg?.organization) {
        const updatedSelection = selectedOrganizations?.some(
          (org) => org.uid === selectedOrg?.organization?.uid
        )
          ? selectedOrganizations?.filter(
              (org) => org.uid !== selectedOrg?.organization?.uid
            )
          : [...selectedOrganizations, selectedOrg?.organization];

        setSelectedOrganizations(updatedSelection.filter(Boolean));
        localStorage.setItem(
          `selectedOrganizations`,
          JSON.stringify(updatedSelection)
        );
      }
    } else {
      if (selectedOrg?.organization) {
        setSelectedSingleOrganization(selectedOrg.organization);
        localStorage.setItem(
          `selectedSingleOrganization`,
          JSON.stringify(selectedOrg.organization)
        );
      }
    }

    setFilterType(FilterType.ORGANIZATION);
  };

  const handleDelete = (orgUid: string) => {
    const updatedSelection = selectedOrganizations.filter(
      (org) => org.uid !== orgUid
    );
    if (selectedOrganizations.length === 1) {
      setFilterType(FilterType.NONE);
    }

    setSelectedOrganizations(updatedSelection);
    localStorage.setItem(
      `selectedOrganizations`,
      JSON.stringify(updatedSelection)
    );

    // Clear all URL params if the deleted organization matches organizationUid param
    const organizationUidParam = searchParams.get("organizationUid");
    if (organizationUidParam === orgUid) {
      setSearchParams({}, { replace: true });
    }
  };

  const getSelectedOrganizations = () => {
    if (isMultiple) {
      return selectedOrganizations.filter(
        (org) => org.tier?.name !== OrganizationTierTypes.FRANCHISE
      );
    }
    return selectedSingleOrganization
      ? [selectedSingleOrganization]
      : [selectedOrganization];
  };

  const renderToggleContent = () => {
    const selectedOrgs = getSelectedOrganizations();

    if (isMultiple && selectedOrgs.length > 0) {
      return (
        <div className="d-flex flex-wrap gap-1">
          {selectedOrgs.map((org) => (
            <Chip
              key={org?.uid}
              label={org?.name || ""}
              onDelete={
                selectedManagers.length === 0
                  ? () => handleDelete(org?.uid || "")
                  : undefined
              }
              size="small"
              sx={{
                textTransform: "capitalize",

                backgroundColor:
                  muiTheme.colorSchemes.light.palette.primary[200],
                cursor: selectedManagers.length > 0 ? "not-allowed" : "pointer",
              }}
            />
          ))}
        </div>
      );
    }

    if (isMultiple) {
      return "Organization";
    }

    const selectedSingleOrg = JSON.parse(
      localStorage.getItem("selectedSingleOrganization") || "{}"
    );

    return selectedSingleOrg?.name || "Organization";
  };

  useEffect(() => {
    if (selectedManagers?.length > 0) {
      setSelectedOrganizations(
        organizationsList ? organizationsList : filteredManagedOrganizations
      );
    }
  }, [
    selectedManagers,
    organizationsList,
    filteredManagedOrganizations,
    setSelectedOrganizations,
  ]);

  return (
    <StyledDropdown
      onSelect={handleSelect}
      screen={screen}
      disabled={isBecomeFranchiseOnly && isMultiple}
    >
      <Dropdown.Toggle
        variant="outline-secondary"
        id="organization-dropdown"
        style={{
          textTransform: "capitalize",
        }}
        disabled={isBecomeFranchiseOnly && isMultiple}
      >
        {renderToggleContent()}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {organizationsList
          ?.filter(
            (org: managedOrganization) =>
              org?.organization?.tier?.name !==
                OrganizationTierTypes.FRANCHISE &&
              !getSelectedOrganizations().some(
                (selected) => selected?.uid === org?.organization?.uid
              )
          )
          ?.map((org: managedOrganization) => (
            <Dropdown.Item
              key={org?.organization?.uid}
              eventKey={org?.organization?.uid}
              style={{
                textTransform: "capitalize",
                cursor:
                  filterType === FilterType.ACCOUNT_MANAGER
                    ? "not-allowed"
                    : "pointer",
              }}
              className={
                getSelectedOrganizations().some(
                  (selected) => selected?.uid === org?.organization?.uid
                )
                  ? "organisation"
                  : ""
              }
            >
              {org?.name || org?.organization?.name}
            </Dropdown.Item>
          ))}
      </Dropdown.Menu>
    </StyledDropdown>
  );
};

export default OrganizationDropdown;
